<script lang="ts">
  /**
   * Two ticks in the corner of your own bubble: have they caught up with it?
   *
   * A GLYPH RATHER THAN A WORD, because the word was the bug. This used to render
   * "Sent" under the thread, which is true of every message on screen and therefore
   * reads as nothing — the not-read state was, in effect, invisible. Ticks that go
   * from dim to lit say it in the corner of the eye, and every reader already knows
   * the convention from the messenger they use all day.
   *
   * TWO, NEVER ONE. A single tick means "the server has it, the device does not", a
   * distinction Skool does not report; claiming it would be inventing a state. Both
   * ticks are always drawn and only the colour carries the answer.
   *
   * SKY, NOT `primary`. These sit on the owner's bubble, which IS `primary` — the blue
   * a messenger lights up with would vanish into its own background, and so does every
   * light tint of it, straight into the dimmed-white not-read tick. `sky-300` here is
   * the same hue as the `sky-500` the Helper draws on Skool's white bubbles; see
   * `TICK_INK` for why one hue across two opposite grounds is the consistent answer
   * rather than one hex.
   *
   * THE TOOLTIP IS OURS, not `title`. The browser's own is slow, unstyled, and cannot
   * be read by touch — and this is the only place the wait ("sent 3 days ago") is
   * spelled out, which is the half of the receipt somebody actually acts on.
   */
  import { TICK_PATHS, TICK_VIEWBOX, receiptTitle } from './ticks';

  let {
    state,
    sentAt = null,
    readAt = null,
    now = Date.now(),
  }: {
    state: 'sent' | 'read';
    /** When we sent it — turns the not-read tick into "sent 3 days ago". */
    sentAt?: string | null;
    /** When they read it. Nothing supplies this yet: Skool reports no read time and
     *  the mirror stores a state rather than a transition. Here so the day it does,
     *  the tooltip is already asking for it. */
    readAt?: string | null;
    now?: number;
  } = $props();

  const label = $derived(receiptTitle(state, { sentAt, readAt, now }));
</script>

<!-- `relative` on the wrapper and `pointer-events-none` on the bubble: the tip is
     positioned against the ticks and must never sit between the reader and the
     message. Right-aligned and above, because this hangs at the right edge of a
     bubble inside a scroller — an unaligned tip is the one that gets clipped. -->
<span class="group relative inline-flex cursor-pointer items-center" aria-label={label}>
  <svg
    class="shrink-0 transition-colors {state === 'read'
      ? 'text-sky-300'
      : 'text-primary-content/45'}"
    width="16"
    height="12"
    viewBox={TICK_VIEWBOX}
    fill="none"
    stroke="currentColor"
    stroke-width="1.9"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    {#each TICK_PATHS as d (d)}
      <path {d} />
    {/each}
  </svg>

  <span
    class="pointer-events-none absolute right-0 bottom-full z-20 mb-1.5 hidden whitespace-nowrap
           rounded-md bg-base-content px-2 py-1 text-[10px] font-medium text-base-100
           opacity-0 shadow-sm transition-opacity group-hover:block group-hover:opacity-100"
    role="tooltip"
  >
    {label}
  </span>
</span>
